import { html, type Html, View } from 'rune-ts';
import { CustomerOrder, ReceiptViewerType } from '../type';
import { flatMap, pipe, reduce, toArray } from '@fxts/core';
import { getDiscountedPrice } from '../util';
import { OrderOrderProcessConstantS } from '../../../../../../modules/Order/OrderProcess/S/Constant/module/OrderOrderProcessConstantS';
import klass from '../ReceiptViewer.module.scss';
import { typo } from '../../../../shared/typography/typo';
import format from 'date-fns/format/index.js';
import { formatCurrency } from '../../../../shared/util/formatCurrency/formatCurrency';
import { makeTopSectionHtml } from '../html';

export interface PurchaseReceiptData {
  type: typeof ReceiptViewerType.PURCHASE;
  customer_order: CustomerOrder;
}

export class PurchaseReceiptViewer extends View<PurchaseReceiptData> {
  protected override template({ customer_order }: PurchaseReceiptData): Html {
    const shipping_orders = pipe(
      customer_order._.store_orders,
      flatMap(({ _: { group_by_shipping_orders } }) => {
        return group_by_shipping_orders;
      }),
      flatMap(({ shipping_orders }) => {
        return shipping_orders;
      }),
      toArray,
    );
    const item_order_up_cs = pipe(
      shipping_orders,
      flatMap(({ _: { item_order_up_cs } }) => {
        return item_order_up_cs;
      }),
      toArray,
    );
    const len = item_order_up_cs.length - 1;
    const shipping_order = shipping_orders[0];
    const product_name = shipping_order?._.item_order_up_cs[0]?.n_info.name;
    const projection_payment = shipping_order?._.projection_payments[0];
    const provider =
      projection_payment?.pg_provider == 'paypal'
        ? 'PayPal'
        : projection_payment?.pg_provider == 'eximbay'
          ? 'AliPay'
          : projection_payment?.pg_provider == 'html5_inicis'
            ? '이니시스'
            : projection_payment?.pg_provider == 'naverpay'
              ? '네이버 페이'
              : '';
    const { total_shipping_price, refunded_total_shipping_price } = reduce(
      (
        mem,
        {
          shipping_price,
          additional_shipping_price,
          refund_shipping_price,
          refund_additional_shipping_price,
        },
      ) => {
        return {
          total_shipping_price: mem.total_shipping_price + shipping_price + additional_shipping_price,
          refunded_total_shipping_price:
            mem.refunded_total_shipping_price + refund_shipping_price + refund_additional_shipping_price,
        };
      },
      {
        total_shipping_price: 0,
        refunded_total_shipping_price: 0,
      },
      shipping_orders,
    );
    const { total_price, cancel_price, price } = pipe(
      item_order_up_cs,
      flatMap(({ order_process_id, _: { up_c_ss } }) => {
        return up_c_ss.map((up) => {
          return { ...up, order_process_id };
        });
      }),
      (up_c_ss) => {
        return reduce(
          (mem, up_c_s) => {
            const total_price = getDiscountedPrice(up_c_s, customer_order.lang) * up_c_s.original_quantity;

            const cancel_price = [
              OrderOrderProcessConstantS.ORDER_PROCESS.PURCHASE,
              OrderOrderProcessConstantS.ORDER_PROCESS.BEFORE_PURCHASE,
            ].includes(up_c_s.order_process_id)
              ? 0
              : total_price;
            const price = total_price - cancel_price;
            return {
              total_price: mem.total_price + total_price,
              cancel_price: mem.cancel_price + cancel_price,
              price: mem.price + price,
            };
          },
          {
            total_price: total_shipping_price,
            cancel_price: refunded_total_shipping_price,
            price: total_shipping_price - refunded_total_shipping_price,
          },
          up_c_ss,
        );
      },
    );

    return html`
      <div class="${klass.root}">
        ${makeTopSectionHtml(this.T.purchase_receipt, this.T.pdf_t, this.T.print_t)}
        <div class="receipt-area-print-pdf">
          <div class="purchase-receipt-customer-order__title">${this.T.title_t}</div>
          <div class="purchase-receipt-customer-order__info ${typo('16_medium')}">
            <div class="purchase-receipt-customer-order__row">
              <div class="purchase-receipt-customer-order__info-left">${this.T.id_t}</div>
              <div class="purchase-receipt-customer-order__info-right">EMCO${customer_order.id}</div>
            </div>
            <div class="purchase-receipt-customer-order__row">
              <div class="purchase-receipt-customer-order__info-left">${this.T.paid_at_t}</div>
              <div class="purchase-receipt-customer-order__info-right">
                ${format(new Date(customer_order.paid_at), 'yyyy.MM.dd HH:mm:ss')}
              </div>
            </div>
            <div class="purchase-receipt-customer-order__row">
              <div class="purchase-receipt-customer-order__info-left">${this.T.provider_t}</div>
              <div class="purchase-receipt-customer-order__info-right">${provider}</div>
            </div>
            <div class="purchase-receipt-customer-order__row">
              <div class="purchase-receipt-customer-order__info-left">${this.T.orderer_t}</div>
              <div class="purchase-receipt-customer-order__info-right">${customer_order.orderer_name}</div>
            </div>
            <div class="purchase-receipt-customer-order__row">
              <div class="purchase-receipt-customer-order__info-left">${this.T.products_t}</div>
              <div class="purchase-receipt-customer-order__info-right">
                ${product_name}${len ? ` 외 ${len}건` : ''}
              </div>
            </div>
            ${cancel_price > 0
              ? html`<div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.total_price_t}</div>
                    <div class="purchase-receipt-customer-order__info-right">
                      ${formatCurrency(total_price, ET.lang)}
                    </div>
                  </div>
                  <div class="purchase-receipt-customer-order__row">
                    <div class="purchase-receipt-customer-order__info-left">${this.T.cancel_price_t}</div>
                    <div
                      class="purchase-receipt-customer-order__info-right purchase-receipt-customer-order__row--red"
                    >
                      -${formatCurrency(cancel_price, ET.lang)}
                    </div>
                  </div>`
              : ''}
            <div class="purchase-receipt-customer-order__row">
              <div
                class="purchase-receipt-customer-order__info-left purchase-receipt-customer-order__row--blue purchase-receipt-customer-order__result-price ${typo(
                  '16_bold',
                )}"
              >
                ${this.T.price_t}
              </div>
              <div class="${klass.result_price} ${typo('20_bold')}">${formatCurrency(price, ET.lang)}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  get T() {
    return {
      purchase_receipt: ET('customer_order::receipt::purchase'),
      title_t: ET('customer_order::receipt::purchase'),
      id_t: ET('customer_order::receipt::id'),
      paid_at_t: ET('customer_order::receipt::paid_at'),
      provider_t: ET('customer_order::receipt::provider'),
      orderer_t: ET('customer_order::receipt::orderer'),
      products_t: ET('customer_order::receipt::products'),
      total_price_t: ET('customer_order::receipt::total_price_t'),
      cancel_price_t: ET('customer_order::receipt::cancel_price_t'),
      price_t: ET('customer_order::receipt::price'),
      pdf_t: ET('customer_order::receipt::pdf'),
      print_t: ET('customer_order::receipt::print'),
    };
  }
}
